import { useDispatch, useSelector } from "react-redux";
import LikertEvaluation from "./LikertEvaluation";
import { editBossComment, editBossCompetency, editEmployeeComment, editEmployeeCompetency, getBossCompetency, getEmployeeCompetency } from "../../../redux/evaluacion_desempenio/EvaluationCompetency/actions";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useEffect, useState } from "react";
import { canEdit, canView, EVALUATION_SPECIFIC_VIEWS, QUARTERS, ROLES, STATUSES } from "../../../pages/evaluacion_desempenio/utils/Constants";

/**
 * Componente para mostrar el formulario de evaluación por competencias.
 * Permite la autoevaluación y la evaluación del jefe, según permisos.
 */
const CompetencyEvaluationForm = ({
    idUser,               // ID del usuario actual
    evaluationEtapa,      // Etapa de la evaluación
    evaluationRole,       // Rol del usuario en la evaluación
    setCompetenciesEvaluationReady // Función para indicar que la evaluación de competencias está lista
}) => {
    const dispatch = useDispatch();
    const { idEvaluacion } = useParams();

    const [employeeComment, setEmployeeComment] = useState("");
    const [bossComment, setBossComment] = useState("");

    // Obtiene los estados y datos de competencias desde Redux
    const {
        loadingEmployeeCompetency,
        saveLoadingEmployeeCompetency,
        employeeCompetencies,
        employeeCompetencyError,

        loadingBossCompetency,
        saveLoadingBossCompetency,
        bossCompetencies,
        bossCompetencyError
    } = useSelector((store) => store.evaluationCompetency);


    const {
        loadingById: loadingEvaluationMonitor,
        evaluationMonitor,
        evaluationMonitorError
    } = useSelector((store) => store.evaluationMonitor);

    // Determinar si el usuario ya ha enviado la evaluación en el 3Q
    const evaluationDone = STATUSES.PENDIENTE_EVALUACION &&
        ((evaluationMonitor?.data_evaluacion_objetivos?.jefe_completo_evaluacion && idUser === evaluationMonitor?.evaluador) ||
            (evaluationMonitor?.data_evaluacion_objetivos?.empleado_completo_evaluacion && idUser === evaluationMonitor?.evaluado));

    //Determinar si el jefe puede ver la evaluación de competencias del empleado
    const canBossSeeEmployeeEvaluation = (evaluationMonitor?.data_evaluacion_objetivos?.empleado_completo_evaluacion && idUser === evaluationMonitor?.evaluador) || idUser === evaluationMonitor?.evaluado;

    useEffect(() => {
        if (evaluationRole === ROLES.EMPLOYEE && employeeCompetencies?.data?.length > 0) {
            // Indica al padre que la evaluación de competencias del empleado está lista
            let allAnswered = employeeCompetencies?.data?.every(item => item.metrica !== null);
            let employeeCommentCompleted = employeeCompetencies?.data?.[0]?.empleado_evaluacion_data?.comentario_general_empleado !== "" && employeeCompetencies?.data?.[0]?.empleado_evaluacion_data?.comentario_general_empleado !== null;
            setCompetenciesEvaluationReady(allAnswered && employeeCommentCompleted);
        }
        if (evaluationRole === ROLES.BOSS && bossCompetencies?.data?.length > 0) {
            // Indica al padre que la evaluación de competencias del jefe está lista
            let allAnswered = bossCompetencies?.data?.every(item => item.metrica !== null);
            let bossCommentCompleted = bossCompetencies?.data?.[0]?.empleado_evaluacion_data?.comentario_general_jefe !== "" && bossCompetencies?.data?.[0]?.empleado_evaluacion_data?.comentario_general_jefe !== null;
            setCompetenciesEvaluationReady(allAnswered && bossCommentCompleted);
        }
    }, [employeeCompetencies, bossCompetencies]);

    /**
     * Handler para actualizar la métrica de una competencia (empleado o jefe).
     * @param {Object} data - Competencia evaluada
     * @param {number} newMetric - Nueva métrica seleccionada
     * @param {number} index - Índice en el array
     */
    const handleCompetencyEvaluationSubmit = async (data, newMetric, index) => {
        let req = {
            evaluacion_empleado: Number(idEvaluacion),
            competencia: data.competencia,
            metrica: newMetric
        }
        // Determina si la edición corresponde al empleado o al jefe
        if (evaluationRole === ROLES.EMPLOYEE) {
            await dispatch(editEmployeeCompetency(req, data.id, index))
            await dispatch(getEmployeeCompetency({ evaluacion_empleado: idEvaluacion }));
        } else if (evaluationRole === ROLES.BOSS) {
            await dispatch(editBossCompetency(req, data.id, index))
            await dispatch(getBossCompetency({ evaluacion_empleado: idEvaluacion }));

        }
    }

    const handleSaveComment = async (data) => {
        if (evaluationRole === ROLES.EMPLOYEE) {
            await dispatch(editEmployeeComment({
                comentario_general_empleado: employeeComment,
                preguntas_completas: true
            }, employeeCompetencies?.data?.[0]?.id))
            await dispatch(getEmployeeCompetency({ evaluacion_empleado: idEvaluacion }));
        } else if (evaluationRole === ROLES.BOSS) {
            await dispatch(editBossComment({
                comentario_general_jefe: bossComment,
                preguntas_completas: true
            }, bossCompetencies?.data?.[0]?.id))
            await dispatch(getBossCompetency({ evaluacion_empleado: idEvaluacion }));
        }
    };

    let allQuestionsAnsweredEmployee = employeeCompetencies?.data?.length > 0 && employeeCompetencies?.data?.every(item => item.metrica !== null);
    let allQuestionsAnsweredBoss = bossCompetencies?.data?.length > 0 && bossCompetencies?.data?.every(item => item.metrica !== null);

    return (
        <div>
            <h3 className="my-5">Evaluación por Competencias</h3>

            {/* Sección de autoevaluación */}
            {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD }) && (
                <div style={{ position: 'relative' }}>
                    {/* Loading overlay para autoevaluación */}
                    {(loadingEmployeeCompetency || saveLoadingEmployeeCompetency) && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '200px'
                            }}
                        >
                            <div className="text-center">
                                <Spinner animation="border" size="sm" className="mr-2" />
                                <span>Cargando...</span>
                            </div>
                        </div>
                    )}

                    <div className="d-flex flex-row">
                        <h5>Autoevaluación</h5>
                    </div>

                    {/* Alerta de error */}
                    <Alert variant="danger" show={employeeCompetencyError}>
                        {employeeCompetencyError}
                    </Alert>

                    {/* Componente de evaluación tipo Likert */}
                    <LikertEvaluation
                        data={employeeCompetencies || []}
                        onSubmit={handleCompetencyEvaluationSubmit}
                        readOnly={(loadingEmployeeCompetency || saveLoadingEmployeeCompetency) || canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) === false || evaluationRole !== ROLES.EMPLOYEE}
                        type={"Competencias"}
                    />
                </div>
            )}

            {/* Sección de evaluación del jefe */}
            {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_COMPETENCY_EVALUATION) && (
                <div style={{ position: 'relative' }}>
                    {/* Loading overlay para evaluación del jefe */}
                    {(loadingBossCompetency || saveLoadingBossCompetency) && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '200px'
                            }}
                        >
                            <div className="text-center">
                                <Spinner animation="border" size="sm" className="mr-2" />
                                <span>Cargando...</span>
                            </div>
                        </div>
                    )}

                    <div className="d-flex flex-row">
                        <h5>Evaluación del Jefe</h5>
                    </div>

                    {/* Alerta de error */}
                    <Alert variant="danger" show={bossCompetencyError}>
                        {bossCompetencyError}
                    </Alert>

                    {/* Componente de evaluación tipo Likert */}
                    <LikertEvaluation
                        data={bossCompetencies || []}
                        onSubmit={handleCompetencyEvaluationSubmit}
                        readOnly={(loadingBossCompetency || saveLoadingBossCompetency) || canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) === false}
                        type={"Competencias"}
                    />
                </div>
            )}

            <div className="my-3">
                <Container fluid>
                    <Row>
                        {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD }) && employeeCompetencies?.data?.length > 0 && (
                            <Col xs={12} md={6} className="mb-3">
                                <Row>
                                    <Col className="text-white bg-dark py-3">
                                        Resultado Autoevaluación
                                    </Col>
                                    <Col className="text-right py-3 bg-light">
                                        {employeeCompetencies?.data?.[0]?.empleado_evaluacion_data?.nota_competencias_empleado_final?.metrica_total || "-"}
                                    </Col>
                                </Row>
                            </Col>
                        )}
                        {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_EVALUATION_RESULTS) && bossCompetencies?.data?.length > 0 && (
                            <Col xs={12} md={6} className="mb-3">
                                <Row>
                                    <Col className="text-white bg-dark py-3">
                                        Resultado Evaluación Jefe
                                    </Col>
                                    <Col className="text-right py-3 bg-light">
                                        {bossCompetencies?.data?.[0]?.empleado_evaluacion_data?.nota_competencias_jefe_final?.metrica_total || "-"}
                                    </Col>
                                </Row>
                            </Col>
                        )}
                    </Row>
                </Container>

                {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD }) && (
                    <div className="my-4 p-3">
                        <Row>
                            <Col className="border p-3 text-center" style={{ fontWeight: "bold" }} xs={9}>CALIFICACIÓN GENERAL DEL EMPLEADO</Col>
                            <Col className="border text-center p-3" xs={3}> {evaluationMonitor?.data_evaluacion_objetivos?.clasificacion_nota_empleado} </Col>
                        </Row>
                        <Row>
                            <Col className="border p-3" style={{ fontWeight: "bold" }} xs={3}>Comentario General Empleado: (250 caracteres incluyendo espacios)</Col>
                            <Col className="bg-light border p-3" xs={9}>
                                <Form.Control
                                    as="textarea"
                                    maxLength={250}
                                    disabled={(loadingEmployeeCompetency || saveLoadingEmployeeCompetency) || (!canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) && evaluationRole === ROLES.EMPLOYEE) || evaluationRole !== ROLES.EMPLOYEE}
                                    value={employeeComment || employeeCompetencies?.data?.[0]?.empleado_evaluacion_data?.comentario_general_empleado || ""}
                                    onChange={(e) => setEmployeeComment(e.target.value)}
                                />
                            </Col>
                        </Row>
                    </div>
                )}

                {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_EVALUATION_RESULTS) && (
                    <div className="my-4 p-3">
                        <Row>
                            <Col className="border p-3 text-center" style={{ fontWeight: "bold" }} xs={9}>CALIFICACIÓN GENERAL DEL JEFE</Col>
                            <Col className="border text-center p-3" xs={3}> {evaluationMonitor?.data_evaluacion_objetivos?.clasificacion_nota_jefe} </Col>
                        </Row>
                        <Row>
                            <Col className="border p-3" style={{ fontWeight: "bold" }} xs={3}>Comentario General Jefe: (250 caracteres incluyendo espacios)</Col>
                            <Col className="bg-light border p-3" xs={9}>
                                <Form.Control
                                    as="textarea"
                                    maxLength={250}
                                    disabled={(loadingBossCompetency || saveLoadingBossCompetency) || !canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD })}
                                    value={bossComment || bossCompetencies?.data?.[0]?.empleado_evaluacion_data?.comentario_general_jefe || ""}
                                    onChange={(e) => setBossComment(e.target.value)}
                                />
                            </Col>
                        </Row>
                    </div>
                )}

                {/*Botón para guardar el comentario*/}
                {((allQuestionsAnsweredBoss && evaluationRole === ROLES.BOSS) ||
                    (allQuestionsAnsweredEmployee && evaluationRole === ROLES.EMPLOYEE))
                    && canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) && (
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="primary"
                            className="rounded-pill"
                            disabled={loadingEmployeeCompetency || saveLoadingEmployeeCompetency || loadingBossCompetency || saveLoadingBossCompetency}
                            onClick={handleSaveComment}
                        >
                            Guardar Comentario
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CompetencyEvaluationForm;